import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Tag,
  Tooltip,
  Descriptions,
} from 'antd';
import {
  SettingOutlined,
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
  FilterOutlined,
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api, { get } from '../../utils/api';

const { TextArea } = Input;
const { Option } = Select;

const SystemConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [viewingConfig, setViewingConfig] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // 配置类型映射
  const configTypeMap = {
    string: '字符串',
    number: '数字',
    boolean: '布尔值',
    json: 'JSON',
  };

  // 配置类型颜色
  const configTypeColor = {
    string: 'blue',
    number: 'green',
    boolean: 'orange',
    json: 'purple',
  };

  useEffect(() => {
    fetchConfigs();
  }, [filters]);

  // 获取配置列表
  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category) {
        params.category = filters.category;
      }
      const res = await get('/admin/configs', params);
      if (res.code === 200) {
        setConfigs(res.data?.configs || []);
      }
    } catch (error) {
      message.error('获取配置列表失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 打开新增弹窗
  const handleAdd = () => {
    setEditingConfig(null);
    form.resetFields();
    form.setFieldsValue({
      configType: 'string',
      isSensitive: false,
    });
    setModalVisible(true);
  };

  // 打开编辑弹窗
  const handleEdit = (config) => {
    setEditingConfig(config);
    
    let configValue = config.config_value;
    // 如果是 JSON 类型，格式化显示
    if (config.config_type === 'json' && configValue) {
      try {
        configValue = JSON.stringify(JSON.parse(configValue), null, 2);
      } catch (e) {
        // 保持原值
      }
    }
    
    form.setFieldsValue({
      configKey: config.config_key,
      configValue: configValue,
      configType: config.config_type,
      description: config.description,
      category: config.category,
      isSensitive: config.is_sensitive === 1,
    });
    setModalVisible(true);
  };

  // 查看详情
  const handleViewDetail = (config) => {
    setViewingConfig(config);
    setDetailModalVisible(true);
  };

  // 删除配置
  const handleDelete = (config) => {
    Modal.confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>确定要删除配置 <code>{config.config_key}</code> 吗？</p>
          <p style={{ color: '#ff4d4f' }}>此操作不可恢复！</p>
        </div>
      ),
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await api.delete(`/admin/configs/${config.config_key}`);
          if (res.code === 200) {
            message.success('配置删除成功');
            fetchConfigs();
          }
        } catch (error) {
          message.error(error.message || '删除失败');
        }
      },
    });
  };

  // 提交表单
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      let configValue = values.configValue;
      
      // 如果是 JSON 类型，验证格式
      if (values.configType === 'json' && configValue) {
        try {
          JSON.parse(configValue);
        } catch (e) {
          message.error('JSON 格式不正确');
          setSubmitting(false);
          return;
        }
      }

      const data = {
        configValue,
        description: values.description,
      };

      if (editingConfig) {
        // 更新配置
        const res = await api.put(`/admin/configs/${editingConfig.config_key}`, data);
        if (res.code === 200) {
          message.success('配置更新成功');
          setModalVisible(false);
          fetchConfigs();
        }
      } else {
        // 新增配置
        const res = await api.post('/admin/configs', {
          configKey: values.configKey,
          configValue,
          configType: values.configType,
          description: values.description,
          category: values.category,
          isSensitive: values.isSensitive ? 1 : 0,
        });
        if (res.code === 200) {
          message.success('配置创建成功');
          setModalVisible(false);
          fetchConfigs();
        }
      }
    } catch (error) {
      message.error(error.message || '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 获取所有分类
  const categories = [...new Set(configs.map(c => c.category).filter(Boolean))];

  // 表格列配置
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '配置键',
      dataIndex: 'config_key',
      key: 'config_key',
      width: 200,
      render: (text) => (
        <Tooltip title={text}>
          <code style={{ fontSize: '12px' }}>{text}</code>
        </Tooltip>
      ),
    },
    {
      title: '配置值',
      dataIndex: 'config_value',
      key: 'config_value',
      ellipsis: true,
      render: (text, record) => {
        if (record.is_sensitive === 1) {
          return <Tag color="red">敏感信息（已隐藏）</Tag>;
        }
        if (!text) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        if (record.config_type === 'boolean') {
          return text === 'true' || text === '1' ? 
            <Tag color="success">是</Tag> : 
            <Tag color="default">否</Tag>;
        }
        if (record.config_type === 'json') {
          return (
            <Tooltip title="点击查看详情">
              <Tag color="purple">JSON 对象</Tag>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={text}>
            <span style={{ 
              maxWidth: '300px', 
              display: 'inline-block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {text}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '类型',
      dataIndex: 'config_type',
      key: 'config_type',
      width: 100,
      render: (type) => (
        <Tag color={configTypeColor[type] || 'default'}>
          {configTypeMap[type] || type}
        </Tag>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category) => category || <span style={{ color: '#999' }}>-</span>,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || <span style={{ color: '#999' }}>-</span>,
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            查看
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <SettingOutlined />
          <span>系统配置管理</span>
        </Space>
      }
      extra={
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            新增配置
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchConfigs}
          >
            刷新
          </Button>
        </Space>
      }
    >
      {/* 筛选条件 */}
      <Space style={{ marginBottom: 16 }}>
        <FilterOutlined />
        <Select
          style={{ width: 200 }}
          placeholder="选择配置分类"
          allowClear
          value={filters.category || undefined}
          onChange={(value) => setFilters({ ...filters, category: value || '' })}
        >
          {categories.map(cat => (
            <Option key={cat} value={cat}>{cat}</Option>
          ))}
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={configs}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条配置`,
          defaultPageSize: 20,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={
          <Space>
            {editingConfig ? <EditOutlined /> : <PlusOutlined />}
            <span>{editingConfig ? '编辑配置' : '新增配置'}</span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        width={700}
      >
        {editingConfig && (
          <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="配置键">
              <code>{editingConfig.config_key}</code>
            </Descriptions.Item>
            <Descriptions.Item label="配置类型">
              <Tag color={configTypeColor[editingConfig.config_type]}>
                {configTypeMap[editingConfig.config_type]}
              </Tag>
            </Descriptions.Item>
            {editingConfig.category && (
              <Descriptions.Item label="分类">
                {editingConfig.category}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {!editingConfig && (
            <>
              <Form.Item
                label="配置键"
                name="configKey"
                rules={[
                  { required: true, message: '请输入配置键' },
                  { pattern: /^[a-zA-Z0-9_.-]+$/, message: '只能包含字母、数字、下划线、点和横线' }
                ]}
                extra="建议使用小写字母和下划线，如：app_name, max_upload_size"
              >
                <Input placeholder="请输入配置键" />
              </Form.Item>

              <Form.Item
                label="配置类型"
                name="configType"
                rules={[{ required: true, message: '请选择配置类型' }]}
              >
                <Select placeholder="请选择配置类型">
                  <Option value="string">字符串</Option>
                  <Option value="number">数字</Option>
                  <Option value="boolean">布尔值</Option>
                  <Option value="json">JSON</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="分类"
                name="category"
                extra="用于分组管理配置，如：system, payment, notification"
              >
                <Input placeholder="请输入分类（可选）" />
              </Form.Item>
            </>
          )}

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.configType !== currentValues.configType
            }
          >
            {({ getFieldValue }) => {
              const configType = editingConfig ? editingConfig.config_type : getFieldValue('configType');
              return (
                <Form.Item
                  label="配置值"
                  name="configValue"
                  rules={[{ required: true, message: '请输入配置值' }]}
                >
                  {configType === 'json' ? (
                    <TextArea
                      rows={8}
                      placeholder='{"key": "value"}'
                      style={{ fontFamily: 'monospace' }}
                    />
                  ) : configType === 'boolean' ? (
                    <Select placeholder="请选择">
                      <Option value="true">是 (true)</Option>
                      <Option value="false">否 (false)</Option>
                    </Select>
                  ) : configType === 'number' ? (
                    <Input type="number" placeholder="请输入数字" />
                  ) : (
                    <Input placeholder="请输入配置值" />
                  )}
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
          >
            <TextArea
              rows={3}
              placeholder="请输入配置描述"
            />
          </Form.Item>

          {!editingConfig && (
            <Form.Item
              label="是否敏感信息"
              name="isSensitive"
              valuePropName="checked"
              extra="敏感信息在列表中会被隐藏显示"
            >
              <Switch checkedChildren="是" unCheckedChildren="否" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 详情弹窗 */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>配置详情</span>
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setDetailModalVisible(false);
              handleEdit(viewingConfig);
            }}
          >
            编辑
          </Button>,
        ]}
        width={700}
      >
        {viewingConfig && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="ID">{viewingConfig.id}</Descriptions.Item>
            <Descriptions.Item label="配置键">
              <code>{viewingConfig.config_key}</code>
            </Descriptions.Item>
            <Descriptions.Item label="配置值">
              {viewingConfig.is_sensitive === 1 ? (
                <Tag color="red">敏感信息（已隐藏）</Tag>
              ) : viewingConfig.config_type === 'json' ? (
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  maxHeight: '300px',
                  overflow: 'auto'
                }}>
                  {viewingConfig.config_value ? 
                    JSON.stringify(JSON.parse(viewingConfig.config_value), null, 2) : 
                    '-'
                  }
                </pre>
              ) : (
                viewingConfig.config_value || '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="配置类型">
              <Tag color={configTypeColor[viewingConfig.config_type]}>
                {configTypeMap[viewingConfig.config_type]}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="分类">
              {viewingConfig.category || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="描述">
              {viewingConfig.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="是否敏感">
              {viewingConfig.is_sensitive === 1 ? (
                <Tag color="red">是</Tag>
              ) : (
                <Tag color="default">否</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {viewingConfig.created_at ? 
                dayjs(viewingConfig.created_at).format('YYYY-MM-DD HH:mm:ss') : 
                '-'
              }
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {viewingConfig.updated_at ? 
                dayjs(viewingConfig.updated_at).format('YYYY-MM-DD HH:mm:ss') : 
                '-'
              }
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </Card>
  );
};

export default SystemConfig;
